import { apiClient, type ApiResponse } from '../../../api/axios';

export interface SignupPayload {
    firstName: string;
    lastName: string;
    otherName: string;
    email: string;
    phoneNo: string;
    password: string;
    titleId: number;
    institutionId: number;
    address: string;
    qualificationId: number;
    sexId: number;
    rankId: number;
    stateId: number;
    roleId: number;
    alreadyHasAnAccount?: boolean;
}

export interface PaginatedResponse<T> {
    data: T[];
    pageNumber: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
}

export type { ApiResponse };

export interface EventType {
    id: number;
    name: string;
    active: boolean;
}

export interface Cycle {
    id: number;
    name: string;
    active: boolean;
    submissionReady: boolean;
}

export interface CycleEvent {
    id: number;
    eventTypeId: number;
    eventType: EventType;
    cycleId: number;
    cycle: Cycle;
    startDate: string;
    endDate: string;
    isActive: boolean;
}

export interface ProfileData {
    userId: string;
    username: string;
    lastLogin: string;
    lastPasswordChange: string | null;
    isVerified: boolean;
    roleId: number;
    personId: string;
    person: {
        personId: string;
        lastName: string;
        firstName: string;
        otherName: string;
        email: string;
        phoneNo: string;
        address: string;
        active: boolean;
        createdDate: string;
        fullName: string;
        qualificationId: number;
        institutionId: number | null;
        institution?: {
            id: number;
            name: string;
            active: boolean;
            isPublic: boolean;
            institutionCategory: string;
            stateId: number;
        };
        passportUrl: string | null;
        titleId: number;
        sexId: number;
        dob: string | null;
        stateId: number;
        rankId: number;
        profileCompleted: boolean;
        affiliateInstitution?: string | null;
        isOtherInstitution?: boolean;
    };
    role: {
        id: number;
        name: string;
        active: boolean;
        slug: string;
    };
    active: boolean;
    createdDate: string;
    signature?: string | null;
}

export interface ProfileUpdatePayload {
    firstName: string;
    lastName: string;
    otherName: string;
    email: string;
    phoneNo: string;
    titleId: number | null;
    institutionId: number | null;
    dob?: string | null;
    address: string;
    qualificationId: number | null;
    sexId: number | null;
    rankId: number | null;
    stateId: number | null;
}

export interface Category {
    id: number;
    name: string;
    active: boolean;
}

export interface SubmissionItem {
    id: string;
    submissionNumber: string;
    title: string;
    authors: string;
    isbnNo: string;
    applicationType: number;
    categoryId: number;
    category?: {
        id: number;
        name: string;
        code: string;
        active: boolean;
        createdDate: string;
    };
    categoryName?: string;
    abstract: string;
    manuscriptUrl: string;
    status?: string;
    statusId?: number;
    statusName?: string;
    submittedAt: string;
    dateSubmitted: string;
    lastUpdated: string;
    plagiarismOutcome?: number;
    issue?: string;
    publicationUrl?: string;
    volume?: string;
    edition?: string;
    keywords?: string;
    isTagDivisionalHeadApproved?: boolean;
    tagDivisionalHeadApprovalDate?: string;
    isDESSApproved?: boolean;
    dessApprovalDate?: string;
    isESApproved?: boolean;
    esApprovalDate?: string;
    isReadyForProcessing?: boolean;
    positionOfJournalRepresentative?: string;
    journalProfessionalAssociation?: string;
}

export interface GetSubmissionsPayload {
    pageNumber: number;
    pageSize: number;
    sortBy: string;
    sortDescending: boolean;
    search: string;
    categoryId: number;
    cycleId: number;
    authorId: string;
    submittedFrom: string | null;
    submittedTo: string | null;
    applicationType: number;
}

export interface AuthorDashboardStats {
    totalSubmissions: number;
    submissionsByApplicationType: Record<string, number>;
    approvedSubmissions: number;
    rejectedSubmissions: number;
    underReviewSubmissions: number;
    submitRevisedCopy: number;
    resubmit: number;
    pendingSubmissions: number;
    pendingReviewedManuscript: number;
}

// ─── Revised Copy Interfaces ──────────────────────────────────────────────────

export interface BookAuthor {
    id: string;
    submissionId: string;
    name: string;
    isActive: boolean;
    isLeadAuthor: boolean;
    affiliateInstitution: string;
}

export interface PendingRevisionRequestDetail {
    submissionRevisionRequestId: string;
    submissionId: string;
    basedOnVersionId: string;
    basedOnVersionUrl: string;
    revisedVersionUrl: string | null;
    comment: string;
    requestedByUserId: string;
    requestedAt: string;
    submittedAt: string | null;
    status: string;
    submissionTitle: string;
    submissionNumber: string;
    manuscriptReviewId: string;
    reviewedManuscriptUrl: string;
    bookAuthors: BookAuthor[];
}

export const authorAuthService = {
    signup: async (payload: SignupPayload): Promise<ApiResponse> => {
        const response = await apiClient.post('/Auth/Signup', payload, { skipAuth: true } as any);
        return response.data;
    },

    verifyAccount: async (code: string): Promise<ApiResponse> => {
        const response = await apiClient.post(`/Auth/VerifyAccount?code=${encodeURIComponent(code)}`, undefined, { skipAuth: true } as any);
        return response.data;
    },

    getActiveCycleEvent: async (): Promise<ApiResponse<CycleEvent>> => {
        const response = await apiClient.get('/EventCycles/GetActiveCycleEvent');
        return response.data;
    },

    // getEventCycleByTypeId: async (eventTypeId: number): Promise<ApiResponse<CycleEvent>> => {
    //     const response = await apiClient.get(`/EventCycles/GetEventCycleBy?EventTypeId=${eventTypeId}`);
    //     return response.data;
    // },

    isCycleRunning: async (): Promise<ApiResponse<boolean>> => {
        const response = await apiClient.get(`Cycles/GetRunningCycle`);
        return { isSuccess: response.data.isSuccess, data: !!response.data.data, message: response.data.message };
    },

    getRunningCycle: async (): Promise<ApiResponse<Cycle>> => {
        const response = await apiClient.get('/Cycles/GetRunningCycle');
        return response.data;
    },

    getUserProfile: async (): Promise<ApiResponse<ProfileData>> => {
        const response = await apiClient.get('/Auth/GetUserProfile');
        return response.data;
    },

    updateProfile: async (payload: FormData): Promise<ApiResponse> => {
        const response = await apiClient.post('/Auth/UpdateProfile', payload, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    },

    getAllCategories: async (status?: boolean): Promise<ApiResponse<Category[]>> => {
        const url = status !== undefined ? `/Category/GetAllCategory?status=${status}` : '/Category/GetAllCategory';
        const response = await apiClient.get(url);
        return response.data;
    },

    createUpdateSubmission: async (payload: FormData): Promise<ApiResponse> => {
        const response = await apiClient.post('/Submissions/CreateUpdateSubmission', payload, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    },

    getSubmissions: async (payload: GetSubmissionsPayload): Promise<ApiResponse<PaginatedResponse<SubmissionItem>>> => {
        const response = await apiClient.post('/Submissions/GetSubmissions', payload);
        return response.data;
    },

    getSubmission: async (id: string): Promise<ApiResponse<SubmissionItem>> => {
        const response = await apiClient.get(`/Submissions/GetSubmission/${id}`);
        return response.data;
    },

    checkUsernameExist: async (email: string): Promise<ApiResponse<boolean>> => {
        const response = await apiClient.get(`/Auth/UsernameExist?email=${encodeURIComponent(email)}`, { skipAuth: true } as any);
        return response.data;
    },

    checkUsernameExistForRole: async (email: string, roleId: number): Promise<ApiResponse<boolean>> => {
        const response = await apiClient.get(`/Auth/UsernameExistForRole?email=${encodeURIComponent(email)}&roleId=${roleId}`, { skipAuth: true } as any);
        return response.data;
    },

    getUserByUsername: async (email: string): Promise<ApiResponse<ProfileData>> => {
        const response = await apiClient.get(`/Auth/GetUserByUsername?userName=${encodeURIComponent(email)}`, { skipAuth: true } as any);
        return response.data;
    },

    getAuthorDashboardStatistics: async (): Promise<ApiResponse<AuthorDashboardStats>> => {
        const response = await apiClient.get('/Statistics/GetAuthorDashboardStatistics');
        return response.data;
    },

    submitRevisedManuscript: async (payload: FormData): Promise<ApiResponse> => {
        const response = await apiClient.post('/Submissions/SubmitRevisedManuscript', payload, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    },

    getSubmissionsByWorkflowStage: async (payload: { pageNumber: number; pageSize: number; sortBy: string; sortDescending: boolean }): Promise<ApiResponse<PaginatedResponse<SubmissionItem>>> => {
        const response = await apiClient.post('/Submissions/GetAuthorSubmissionByWorkFlowStage', payload);
        return response.data;
    },

    getManuscriptsByWorkflowStage: async (payload: { pageNumber: number; pageSize: number; sortBy: string; sortDescending: boolean }): Promise<ApiResponse<PaginatedResponse<any>>> => {
        const response = await apiClient.post('/Reviews/GetAuthorManuscriptByWorkFlowStage', payload);
        return response.data;
    },

    getSubmissionById: async (submissionId: string): Promise<ApiResponse<any>> => {
        const response = await apiClient.get(`/Submissions/GetSubmissionById?submissionId=${submissionId}`);
        return response.data;
    },

    getReviewerAssessment: async (reviewAssignmentId: string): Promise<ApiResponse<any>> => {
        const response = await apiClient.get(`/Reviews/GetReviewerAssessment?reviewAssignmentId=${reviewAssignmentId}`);
        return response.data;
    },

    moveSubmissionToTetfund: async (payload: { submissionId: string; comment: string }): Promise<ApiResponse<boolean>> => {
        const response = await apiClient.post('/Submissions/MoveSubmissionToTetfund', payload);
        return response.data;
    },

    moveManuscriptReviewToTetfund: async (payload: { manuscriptReviewId: string; comment: string }): Promise<ApiResponse<boolean>> => {
        const response = await apiClient.post('/ReviewerEvaluations/MoveManuscriptReviewToTetfund', payload);
        return response.data;
    },

    getPendingRevisionRequestDetail: async (submissionId: string): Promise<ApiResponse<PendingRevisionRequestDetail>> => {
        const response = await apiClient.get(
            `https://tetfundtagapi.lloydant.com/api/RevisedCopy/GetPendingRevisionRequestDetailBySubmissionId?submissionId=${submissionId}`
        );
        return response.data;
    },

    submitRevisedCopyManuscript: async (payload: FormData): Promise<ApiResponse<boolean>> => {
        const response = await apiClient.post(
            'https://tetfundtagapi.lloydant.com/api/RevisedCopy/SubmitRevisedManuscript',
            payload,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        return response.data;
    },
};
